import * as React from "react";
import { useMemo, useState } from "react";
import { Edit3 } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import TableTemplate from "../common/TableTemplate";
import { CostsModal } from "../products/modals/CostsModal";
import { generateDynamicColumns } from "../../utils/generateDynamicColumns";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import ChartViewTemplate from "../charts/ChartViewTemplate";
import {
  useUpdateAllCostsForProductMutation,
  useUpdateCostAndSupplyMutation,
} from "../../services/product";
import { columnHelper } from "../../utils/utils";
import ProductAdStatsContainer from "./ProductAdStatsContainer";

export function ProductAnalytics({
  variantTable,
  campaignsTable,
  variantSalesChartData,
}) {
  const urlParams = useParams();

  const [isCostsOpen, setIsCostsOpen] = useState(false);
  const [variantGlid, setVariantGlid] = useState(null);

  const [updateCostAndSupply] = useUpdateCostAndSupplyMutation();

  const [updateAllCostsForProduct] = useUpdateAllCostsForProductMutation();

  const columns = useMemo(
    () =>
      generateDynamicColumns({
        calledBy: "variant",
        columnHelper,
        headerData: variantTable.Column_header,
        cellEventHandlers: {
          costs: {
            shouldEnableInteraction: () => true,
            onClick: (cell) => {
              setVariantGlid(cell.row.original.glid);
              setIsCostsOpen(true);
            },
          },
        },
        editableFields: {
          average_estimated_cost: {
            editIcon: Edit3,
            editIconClassNames: "w-4 h-4",
            inputType: "number",
            inputProps: {
              min: 0,
            },
            onSave: async (key, originalRow, newValue) => {
              const variantGlid = originalRow.glid.display;
              const result = await updateCostAndSupply([
                {
                  class: "Variant/Cost",
                  glid: variantGlid,
                  update: {
                    cost_supplier: Number(newValue),
                  },
                },
              ]);
              if ("error" in result) {
                toast.error("Something went wrong. Please try again.");
                console.log("Failed updating cost: ", result.error);
                return;
              }
              toast.success("Cost updated successfully.");
            },
            onCancel: (originalRow) => {
              console.log("edit operation cancelled on the row: ", originalRow);
            },
            onHeaderSave: async (key, newValue) => {
              const productId = urlParams.id;
              const result = await updateAllCostsForProduct([
                {
                  class: "Product/Cost",
                  glid: productId,
                  update: {
                    cost_estimated: Number(newValue),
                  },
                },
              ]);

              if ("error" in result) {
                toast.error("Something went wrong. Please try again.");
                console.log(
                  "Failed updating cost for all products: ",
                  result.error
                );
                return;
              }
              toast.success("Costs updated successfully.");
            },
            onHeaderCancel: (key) => {
              console.log("Header edit cancelled for:", key);
            },
          },
          average_supplier_cost: {
            inputType: "number",
            inputProps: {},
            onSave: async (key, originalRow, newValue) => {
              const variantGlid = originalRow.glid.display;
              const result = await updateCostAndSupply([
                {
                  class: "Variant/Cost",
                  glid: variantGlid,
                  update: {
                    cost_supplier: Number(newValue),
                  },
                },
              ]);
              if ("error" in result) {
                toast.error("Something went wrong. Please try again.");
                console.log("Failed updating cost: ", result.error);
                return;
              }
              toast.success("Cost updated successfully.");
            },
            onCancel: (originalRow) => {
              console.log("edit operation cancelled on the row: ", originalRow);
            },
            onHeaderSave: async (key, newValue) => {
              const productId = urlParams.id;
              const result = await updateAllCostsForProduct([
                {
                  class: "Product/Cost",
                  glid: productId,
                  update: {
                    cost_supplier: Number(newValue),
                  },
                },
              ]);

              if ("error" in result) {
                toast.error("Something went wrong. Please try again.");
                console.log(
                  "Failed updating cost for all products: ",
                  result.error
                );
                return;
              }
              toast.success("Costs updated successfully.");
            },
            onHeaderCancel: (key) => {
              console.log("Header edit cancelled for:", key);
            },
          },
          dropDown: {
            inputType: "select",
            onSave: async (key, originalRow, newValue) => {
              // For now, just update the local state since there's no API
              originalRow[key] = {
                ...originalRow[key],
                selected: newValue,
                display: newValue,
              };
              toast.success("Dropdown selection updated");
            },
          },
          richDropDown: {
            inputType: "select",
            onSave: async (key, originalRow, newValue) => {
              // For now, just update the local state
              const selectedOption = originalRow[key].options.find(
                (opt) => opt.account_id === newValue
              );
              originalRow[key] = {
                ...originalRow[key],
                selected: newValue,
                display: selectedOption?.name || newValue,
              };
              toast.success("Rich dropdown selection updated");
            },
          },
          files: {
            onSave: async (key, originalRow, newValue) => {
              // For now, just update the local state
              originalRow[key] = {
                ...originalRow[key],
                asset: newValue,
              };
              toast.success("Files updated");
            },
          },
        },
      }),
    [variantTable, urlParams.id, updateCostAndSupply, updateAllCostsForProduct]
  );

  const camColumns = useMemo(
    () =>
      generateDynamicColumns({
        columnHelper,
        headerData: campaignsTable.Column_header,
        navigationConfig: {
          ads: {
            navigable: true,
            getUrl: ({ originalRow }) => {
              const campaignGlid = originalRow.glid.display;
              const productId = urlParams.id;
              return {
                pathname: `/product/${productId}/campaign/${campaignGlid}`,
                state: null,
              };
            },
          },
        },
        editableFields: {
          budget: {
            inputType: "number",
            props: {
              min: 0,
            },
            onHeaderSave: (key, newValue) => {
              console.log("data: ", { key, newValue });
            },
          },
          status: {
            inputType: "select",
            onHeaderSave: (key, newValue) => {
              console.log("data: ", { key, newValue });
            },
          },
        },
        calledBy: "cam",
      }),
    [campaignsTable.Column_header, urlParams.id]
  );

  return (
    <div className="bg-white rounded-lg mt-2">
      <div className="transition-all duration-200 ease-in-out overflow-hidden opacity-100">
        <div className=" pb-4">
          <Tabs defaultValue="variants" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="variants">Variants</TabsTrigger>
              <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
              <TabsTrigger value="stats">Stats</TabsTrigger>
            </TabsList>

            <TabsContent value="variants">
              <TableTemplate
                columns={columns || []}
                data={variantTable.data || []}
                options={{
                  enableGlobalFilter: true,
                  enablePagination: true,
                  enableColumnVisibility: true,
                  enableSorting: true,
                  enableRowSelection: true,
                  enableColumnFilters: true,
                  enableColumnOrder: true,
                  enableColumnResize: true,
                  defaultPageSize: 50,
                  showTotalItems: true,
                  showPreferences: true,
                }}
                layout="standard"
              />
              <div className="mt-2">
                <ChartViewTemplate
                  title="Product Sales Distribution"
                  description="Sales by Variant"
                  data={variantSalesChartData}
                  footerText="Showing total sales across all variants"
                />
              </div>
            </TabsContent>

            <TabsContent value="campaigns">
              <TableTemplate
                // this is safe, because if the data is present, we never create an inline array and it doesn't lead to unnecessary rerendering
                columns={camColumns || []}
                data={campaignsTable.data || []}
                options={{
                  enableGlobalFilter: true,
                  enablePagination: true,
                  enableColumnVisibility: true,
                  enableSorting: true,
                  enableColumnFilters: true,
                  enableColumnOrder: true,
                  enableColumnResize: true,
                  defaultPageSize: 51,
                  showTotalItems: true,
                  showPreferences: true,
                }}
                layout="standard"
              />
            </TabsContent>

            <TabsContent value="stats">
              <ProductAdStatsContainer />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <CostsModal
        isOpen={isCostsOpen}
        variantGlid={variantGlid?.display}
        onClose={() => {
          setVariantGlid(null);
          setIsCostsOpen(false);
        }}
      />
    </div>
  );
}

export default ProductAnalytics;
